import React, { useState } from "react";
import emailjs from "emailjs-com";
import toast, { Toaster } from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import {
  HiOutlineEnvelope,
  HiOutlinePaperAirplane,
  HiXMark,
} from "react-icons/hi2";

const FloatingEmail = () => {
  const [isOpen, setIsOpen] = useState(false);

  const sendEmail = async (event) => {
    event.preventDefault();

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID?.trim();
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID?.trim();
    const publicKey =
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.trim() ||
      process.env.REACT_APP_EMAILJS_USER_ID?.trim();

    if (!serviceId || !templateId || !publicKey) {
      const missing = [
        !serviceId && "REACT_APP_EMAILJS_SERVICE_ID",
        !templateId && "REACT_APP_EMAILJS_TEMPLATE_ID",
        !publicKey &&
          "REACT_APP_EMAILJS_PUBLIC_KEY or REACT_APP_EMAILJS_USER_ID",
      ]
        .filter(Boolean)
        .join(", ");

      console.error("Missing EmailJS environment variables:", missing);
      toast.error("Email service is not configured yet. Restart the app after updating .env.");
      return;
    }

    try {
      await emailjs.sendForm(serviceId, templateId, event.target, publicKey);
      toast.success("Your message has been sent successfully.");
      event.target.reset();
      setIsOpen(false);
    } catch (error) {
      console.error("EmailJS send failed:", error);
      toast.error("Failed to send email. Please try again.");
    }
  };

  return (
    <>
      <div className="pointer-events-none fixed bottom-4 left-4 right-4 z-[70] flex justify-end sm:bottom-6 sm:left-auto sm:right-6">
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="floating-email-panel"
              initial={{ opacity: 0, y: 28, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.97 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-[1.75rem] border border-white/12 bg-[#08111d]/94 shadow-[0_24px_70px_rgba(2,6,23,0.55)] backdrop-blur-2xl"
            >
              <div className="border-b border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.18),transparent_42%),linear-gradient(180deg,rgba(15,23,42,0.92),rgba(8,17,31,0.82))] px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.28em] text-[#f59e0b]">
                      Quick Email
                    </p>
                    <h3 className="mt-2 font-['Sora'] text-xl text-white">
                      Send your project idea
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-slate-400">
                      Share a brief, role, or collaboration idea and I&apos;ll reply soon.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-300 transition hover:border-white/20 hover:text-white"
                    aria-label="Close floating email form"
                  >
                    <HiXMark className="text-lg" />
                  </button>
                </div>
              </div>

              <form onSubmit={sendEmail} className="grid gap-4 px-5 py-5">
                <label className="grid gap-2 text-sm text-slate-300">
                  Name
                  <input
                    required
                    type="text"
                    name="from_name"
                    placeholder="Your name"
                    className="rounded-2xl border border-white/10 bg-[#050b14]/70 px-4 py-3.5 text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-cyan-300/50"
                  />
                </label>

                <label className="grid gap-2 text-sm text-slate-300">
                  Email
                  <input
                    required
                    type="email"
                    name="from_email"
                    placeholder="your@email.com"
                    className="rounded-2xl border border-white/10 bg-[#050b14]/70 px-4 py-3.5 text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-cyan-300/50"
                  />
                </label>

                <label className="grid gap-2 text-sm text-slate-300">
                  Message
                  <textarea
                    required
                    name="message"
                    rows="5"
                    placeholder="Tell me what you want to build."
                    className="rounded-[1.35rem] border border-white/10 bg-[#050b14]/70 px-4 py-3.5 text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-cyan-300/50"
                  />
                </label>

                <div className="flex items-center justify-between gap-3 pt-1">
                  <a
                    href="mailto:iamsinghshubhamm@gmail.com"
                    className="text-xs uppercase tracking-[0.22em] text-slate-500 transition hover:text-slate-300"
                  >
                    Open mail app
                  </a>

                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 rounded-full bg-[#f59e0b] px-5 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-slate-950 transition hover:bg-[#fbbf24]"
                  >
                    <HiOutlinePaperAirplane className="text-base" />
                    Send
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.button
              key="floating-email-button"
              type="button"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onClick={() => setIsOpen(true)}
              className="pointer-events-auto inline-flex items-center gap-3 rounded-full border border-white/12 bg-[linear-gradient(135deg,rgba(245,158,11,0.96),rgba(251,191,36,0.92))] px-3 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-slate-950 shadow-[0_18px_45px_rgba(245,158,11,0.3)] transition hover:-translate-x-1 hover:bg-[linear-gradient(135deg,rgba(245,158,11,1),rgba(251,191,36,0.96))] hover:shadow-[0_24px_70px_rgba(245,158,11,0.55)]"
              aria-label="Open floating email form"
            >
              <span className="flex h-9 w-8 items-center justify-center rounded-full bg-white/25">
                <HiOutlineEnvelope className="text-lg" />
              </span>
              <span>Email Me</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <Toaster position="bottom-center" />
    </>
  );
};

export default FloatingEmail;
